/**
 * Define la estructura de un enlace social mostrado en la barra superior.
 *
 * Propósito: modelar de forma tipada cada red social configurable desde CMS.
 */
export interface TopbarRedSocial {
  nombre: string;
  iconoClase: string;
  url: string;
  visible: boolean;
}

/**
 * Define la estructura del bloque de teléfono en el topbar.
 *
 * Propósito: permitir activar/desactivar el contacto telefónico y su contenido.
 */
export interface TopbarTelefono {
  visible: boolean;
  numero: string;
}

/**
 * Define un segmento de texto para el título del topbar.
 * 
 * Propósito: permitir configurar partes del título con estilo primario.
 */
export interface TopbarTituloSegmento {
  texto: string;
  esPrimario: boolean;
}

/**
 * Define la estructura del bloque de correo en el topbar.
 *
 * Propósito: permitir activar/desactivar el contacto por email y su contenido.
 */
export interface TopbarCorreo {
  visible: boolean;
  email: string;
}

export interface TopbarWhatsapp {
  visible: boolean;
  numero: string;
  mensaje: string;
}

export interface TopbarDireccion {
  visible: boolean;
  texto: string;
  urlMapa: string;
}

/**
 * Define la estructura del bloque de redes sociales en el topbar.
 *
 * Propósito: agrupar la configuración general y la lista de redes.
 */
export interface TopbarRedes {
  visible: boolean;
  items: TopbarRedSocial[];
}

/**
 * Estructura JSON persistida para la configuración del topbar.
 *
 * Propósito: almacenar el contenido dinámico de la franja superior del layout.
 */
export interface TopbarCmsData {
  visible: boolean;
  telefono: TopbarTelefono;
  correo: TopbarCorreo;
  whatsapp: TopbarWhatsapp;
  direccion: TopbarDireccion;
  redes: TopbarRedes;
  titulo: TopbarTituloSegmento[];
}

/**
 * Registro completo asociado a la tabla/configuración del topbar.
 *
 * Propósito: modelar respuestas del backend con `id` y `data`.
 */
export interface TopbarCmsRegistro {
  id: number;
  data: TopbarCmsData;
}

/**
 * Retorna la configuración por defecto del topbar.
 *
 * Flujo: construye una estructura válida para fallback local y formularios.
 *
 * @returns Configuración completa inicial del topbar.
 */
export function obtenerTopbarCmsPorDefecto(): TopbarCmsData {
  return {
    visible: true,
    telefono: {
      visible: false,
      numero: ''
    },
    correo: {
      visible: false,
      email: ''
    },
    whatsapp: {
      visible: false,
      numero: '',
      mensaje: 'Hola, me gustaría solicitar información.'
    },
    direccion: {
      visible: false,
      texto: '',
      urlMapa: ''
    },
    titulo: [{ texto: 'Tu centro', esPrimario: true }],
    redes: {
      visible: false,
      items: [
        { nombre: 'Facebook', iconoClase: 'fab fa-facebook-f', url: '', visible: true },
        { nombre: 'Twitter', iconoClase: 'fab fa-twitter', url: '', visible: true },
        { nombre: 'LinkedIn', iconoClase: 'fab fa-linkedin-in', url: '', visible: true },
        { nombre: 'Instagram', iconoClase: 'fab fa-instagram', url: '', visible: true },
        { nombre: 'YouTube', iconoClase: 'fab fa-youtube', url: '', visible: true }
      ]
    }
  };
}

/**
 * Normaliza una configuración parcial del topbar y garantiza estructura completa.
 *
 * Flujo: mezcla valores recibidos desde API con defaults para evitar campos nulos.
 *
 * @param data Configuración parcial a normalizar.
 * @returns Configuración tipada y consistente para consumo de UI.
 */
export function normalizarTopbarCmsData(data?: Partial<TopbarCmsData> | null): TopbarCmsData {
  const porDefecto = obtenerTopbarCmsPorDefecto();
  const redesBase = porDefecto.redes.items;
  const redesEntrada = data?.redes?.items ?? [];

  const redesNormalizadas = redesEntrada.length
    ? redesEntrada.map((redEntrada, indice) => {
        const redDefault = redesBase[indice] ?? {
          nombre: 'Enlace',
          iconoClase: 'fa fa-link',
          url: '',
          visible: true
        };
        return {
          nombre: redEntrada.nombre ?? redDefault.nombre,
          iconoClase: redEntrada.iconoClase ?? redDefault.iconoClase,
          url: redEntrada.url ?? redDefault.url,
          visible: redEntrada.visible ?? redDefault.visible
        };
      })
    : redesBase;

  return {
    visible: data?.visible ?? porDefecto.visible,
    telefono: {
      visible: data?.telefono?.visible ?? porDefecto.telefono.visible,
      numero: data?.telefono?.numero ?? porDefecto.telefono.numero
    },
    correo: {
      visible: data?.correo?.visible ?? porDefecto.correo.visible,
      email: data?.correo?.email ?? porDefecto.correo.email
    },
    whatsapp: {
      visible: data?.whatsapp?.visible ?? porDefecto.whatsapp.visible,
      numero: data?.whatsapp?.numero ?? porDefecto.whatsapp.numero,
      mensaje: data?.whatsapp?.mensaje ?? porDefecto.whatsapp.mensaje
    },
    direccion: {
      visible: data?.direccion?.visible ?? porDefecto.direccion.visible,
      texto: data?.direccion?.texto ?? porDefecto.direccion.texto,
      urlMapa: data?.direccion?.urlMapa ?? porDefecto.direccion.urlMapa
    },
    titulo: (data?.titulo ?? porDefecto.titulo).map((t) => ({
      texto: t.texto ?? '',
      esPrimario: t.esPrimario ?? false
    })),
    redes: {
      visible: data?.redes?.visible ?? porDefecto.redes.visible,
      items: redesNormalizadas
    }
  };
}
